import axios from 'axios';
import { DOMAIN } from '../constants';
import buildAuthHeaders from '../../utils/apiHeaders';
import {
  SOCIAL_PENDING_REQUESTS_SET,
  SOCIAL_PRESENCE_MERGE,
  SOCIAL_DM_UNREAD_SET,
} from '../types/socialTypes';
import { fetchUnreadCount } from '../../utils/dmApi';

// Use the house helper, NOT a hand-rolled header builder. The access token
// lives at `userInfo.access`; an earlier version of this file read
// `userInfo.token`, which is always undefined. Every social request then went
// out unauthenticated, 401'd, tripped the axios refresh interceptor, and
// retried - an unbounded 401/refresh storm (measured at ~3100 requests in 8
// seconds) that re-rendered the app continuously and made unrelated pages fail
// under test. buildAuthHeaders also attaches X-Device-Id, which the hand-rolled
// version dropped.
const authConfig = (getState) => buildAuthHeaders(getState().userLogin?.userInfo);

export const fetchFollowRequests = (direction = 'incoming') => async (_, getState) => {
  const config = authConfig(getState);
  const { data } = await axios.get(
    `${DOMAIN}/api/v1/engagement/follow-requests/?direction=${direction}`,
    config,
  );
  return Array.isArray(data) ? data : [];
};

export const fetchPendingRequestCount = () => async (dispatch, getState) => {
  try {
    const config = authConfig(getState);
    const { data } = await axios.get(`${DOMAIN}/api/v1/engagement/follow-requests/count/`, config);
    dispatch({ type: SOCIAL_PENDING_REQUESTS_SET, payload: data.pending_count || 0 });
    return data.pending_count || 0;
  } catch {
    return 0;
  }
};

export const acceptFollowRequest = (username) => async (dispatch, getState) => {
  const config = authConfig(getState);
  const { data } = await axios.post(
    `${DOMAIN}/api/v1/engagement/follow-requests/${username}/accept/`, {}, config,
  );
  dispatch(fetchPendingRequestCount());
  return data;
};

export const declineFollowRequest = (username) => async (dispatch, getState) => {
  const config = authConfig(getState);
  const { data } = await axios.post(
    `${DOMAIN}/api/v1/engagement/follow-requests/${username}/decline/`, {}, config,
  );
  dispatch(fetchPendingRequestCount());
  return data;
};

export const blockUser = (username) => async (_, getState) => {
  const config = authConfig(getState);
  const { data } = await axios.post(`${DOMAIN}/api/v1/engagement/block/${username}/`, {}, config);
  return data;
};

export const unblockUser = (username) => async (_, getState) => {
  const config = authConfig(getState);
  const { data } = await axios.delete(`${DOMAIN}/api/v1/engagement/block/${username}/`, config);
  return data;
};

export const fetchPrivacySettings = () => async (_, getState) => {
  const config = authConfig(getState);
  const { data } = await axios.get(`${DOMAIN}/api/v1/engagement/settings/privacy/`, config);
  return data;
};

export const updatePrivacySettings = (patch) => async (_, getState) => {
  const config = authConfig(getState);
  const { data } = await axios.put(`${DOMAIN}/api/v1/engagement/settings/privacy/`, patch, config);
  return data;
};

/**
 * Batch presence pull.
 *
 * This is a POLL, not a subscription - there is deliberately no
 * `presence:` WebSocket channel. A subscription could not be revoked from an
 * HTTP handler (ws.ts's unsubscribe primitives take a socket, and a block or
 * unfollow only knows a userId), so a subscriber would keep receiving presence
 * after losing the right to see it. Pulling re-evaluates visibility on the
 * server every time, so revocation is automatic.
 *
 * Callers pass ONLY viewport-visible ids, capped at 100 by the server.
 */
export const fetchPresence = (userIds) => async (dispatch, getState) => {
  const ids = Array.from(new Set((userIds || []).filter(Boolean))).slice(0, 100);
  if (ids.length === 0) return {};
  try {
    const config = authConfig(getState);
    const { data } = await axios.post(
      `${DOMAIN}/api/v1/engagement/presence/`, { user_ids: ids }, config,
    );
    const byId = {};
    (data.presence || []).forEach((p) => {
      byId[p.user_id] = { is_online: p.is_online, last_seen_at: p.last_seen_at };
    });
    dispatch({ type: SOCIAL_PRESENCE_MERGE, payload: byId });
    return byId;
  } catch {
    return {};
  }
};

// Module-level coalescing window for the unread counter.
//
// THREE independent subscribers refetch this on every inbound `dm.message`:
// the navbar button, the inbox page, and the open thread. A 50-message burst
// would therefore fire 150 requests in a few seconds. The counter is a badge -
// it is never worth more than one request per window - so an in-flight or
// recent fetch short-circuits the rest. This repo has been burned by request
// storms before (see the header of this file); coalescing is cheaper than
// discovering the next one in prod.
const DM_UNREAD_MIN_INTERVAL_MS = 1500;
let dmUnreadLastAt = 0;
let dmUnreadInFlight = null;
// The window is module state, so it outlives the user. Without this, a logout
// -> login inside 1500ms would have the new account's first badge fetch
// swallowed by the previous account's window, leaving the OTHER user's count
// on screen until something else happened to trigger a refetch.
let dmUnreadToken = null;

/**
 * Refresh the DM unread counters into the social slice.
 *
 * Lives here rather than in dmApi.js because it is a *badge* concern: the
 * navbar inbox button and the desktop sidebar row both read it from redux,
 * neither of them mounts the DM pages, and both need it fresh on login. The
 * request shape itself still goes through dmApi so there is one place that
 * knows the endpoint.
 *
 * Swallows errors and returns null - a failed count must never surface a
 * toast or block a render. The next reconnect refetches it.
 *
 * Pass `{ force: true }` to bypass the coalescing window for a user-initiated
 * action whose whole point is an immediate correction (accepting a request,
 * opening a thread).
 */
export const fetchDmUnread = ({ force = false } = {}) => async (dispatch, getState) => {
  const userInfo = getState().userLogin?.userInfo;
  if (!userInfo?.access || userInfo.isAnon) return null;
  // A different account is a different counter: drop the window and any
  // in-flight request belonging to the previous user.
  if (dmUnreadToken !== userInfo.access) {
    dmUnreadToken = userInfo.access;
    dmUnreadLastAt = 0;
    dmUnreadInFlight = null;
  }
  if (!force) {
    // Join an in-flight request rather than starting a second one.
    if (dmUnreadInFlight) return dmUnreadInFlight;
    if (Date.now() - dmUnreadLastAt < DM_UNREAD_MIN_INTERVAL_MS) return null;
  }
  dmUnreadLastAt = Date.now();
  let promise;
  const run = async () => {
    try {
      const { unreadTotal, unreadConversations } = await fetchUnreadCount(userInfo);
      dispatch({
        type: SOCIAL_DM_UNREAD_SET,
        payload: { conversations: unreadConversations, messages: unreadTotal },
      });
      return { conversations: unreadConversations, messages: unreadTotal };
    } catch {
      return null;
    } finally {
      // Only clear the slot if it is still OURS. A forced call can overwrite
      // it while this one is still running, and an unconditional null here
      // would then release the newer request's slot early.
      if (dmUnreadInFlight === promise) dmUnreadInFlight = null;
    }
  };
  promise = run();
  dmUnreadInFlight = promise;
  return promise;
};
